"use client"

import { cx } from "@shared/components/untitledui/util/cx"
import type { ReactNode } from "react"
import {
  ColorSwatchPicker as AriaColorSwatchPicker,
  ColorSwatchPickerItem as AriaColorSwatchPickerItem,
  type ColorSwatchPickerProps,
} from "react-aria-components"

/**
 * A single-select grid of color swatches built on react-aria's `ColorSwatchPicker`:
 * roving arrow-key navigation, `aria-selected` on the active swatch, and a real
 * selection value (`value`/`onChange` as a parsed color) — instead of a hand-rolled
 * `<button>` grid with a fake ring.
 *
 * Each item renders a caller-supplied glyph (the "A", the highlighter box, the
 * underline bar) rather than a solid chip, so the same picker serves text color,
 * highlight, and underline. The `color` prop still drives selection matching, so the
 * glyph is purely visual.
 */
export function ColorSwatchPicker({
  className,
  children,
  ...props
}: Omit<ColorSwatchPickerProps, "layout" | "className"> & {
  className?: string
  children: ReactNode
}) {
  return (
    <AriaColorSwatchPicker
      layout="grid"
      // Content-sized columns, not `1fr`: inside a shrink-to-fit popover, fractional
      // columns collapse to 0 and the swatches overlap. Arrow-key nav reads geometry.
      className={cx("grid w-max grid-cols-[repeat(5,auto)] gap-0.5", className)}
      {...props}
    >
      {children}
    </AriaColorSwatchPicker>
  )
}

/**
 * One swatch. `color` is the value react-aria matches selection against (pass a
 * transparent color for a "default / clear" swatch). `aria-label` names it for
 * assistive tech, since the glyph carries no color description of its own.
 */
export function ColorSwatchOption({
  color,
  ariaLabel,
  className,
  children,
}: {
  color: string
  ariaLabel: string
  className?: string
  children: ReactNode
}) {
  return (
    <AriaColorSwatchPickerItem
      color={color}
      aria-label={ariaLabel}
      className={cx(
        // The glyph is sized and clipped to the cell so it can't overflow onto (and
        // steal clicks from) a neighboring swatch. Underline overrides to size-6.
        "flex size-9 cursor-pointer items-center justify-center overflow-hidden rounded-md outline-brand [&_svg]:size-5",
        "hover:bg-gray-100",
        "focus-visible:outline-2 focus-visible:outline-offset-1",
        "selected:bg-blue-100",
        className,
      )}
    >
      {children}
    </AriaColorSwatchPickerItem>
  )
}
